import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiMessageCircle,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUser,
} from "react-icons/fi";
import api from "../../utills/api";

const WhatsApp = () => {
  const [leads, setLeads] = useState([]);
  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);

  const getArray = (response) => {
    const data = response?.data?.data;

    if (Array.isArray(data)) return data;

    if (Array.isArray(data?.leads)) return data.leads;

    if (Array.isArray(data?.items)) return data.items;

    if (Array.isArray(data?.docs)) return data.docs;

    return [];
  };

  const loadWhatsAppData = async () => {
    try {
      setLoading(true);

      const leadsResponse = await api.get("/leads");

      const leadList = getArray(leadsResponse);

      setLeads(leadList);

      /*
       * Existing Lead Details API contains activities.
       * We use those activities to identify WhatsApp
       * communication already recorded against leads.
       */
      const results = await Promise.all(
        leadList.slice(0, 100).map(async (lead) => {
          const leadId = lead?._id || lead?.id;

          if (!leadId) return [];

          try {
            const response = await api.get(
              `/leads/${leadId}`
            );

            const data = response?.data?.data;

            const leadActivities = Array.isArray(
              data?.activities
            )
              ? data.activities
              : [];

            return leadActivities
              .filter((activity) => {
                const type = String(
                  activity?.type || ""
                ).toLowerCase();

                return type.includes("whatsapp");
              })
              .map((activity) => ({
                ...activity,
                leadId,
                lead,
              }));
          } catch (error) {
            console.error(
              `Failed to load WhatsApp activities for ${leadId}`,
              error
            );

            return [];
          }
        })
      );

      setActivities(results.flat());
    } catch (error) {
      console.error(
        "Failed to load WhatsApp data:",
        error
      );

      setLeads([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWhatsAppData();
  }, []);

  const getLeadName = (lead) => {
    if (!lead) return "Unknown Lead";

    return (
      lead?.name ||
      `${lead?.firstName || ""} ${
        lead?.lastName || ""
      }`.trim() ||
      "Unknown Lead"
    );
  };

  const getPhone = (lead) => {
    return (
      lead?.whatsapp ||
      lead?.whatsappNumber ||
      lead?.mobile ||
      lead?.phone ||
      ""
    );
  };

  const normalizePhone = (phone) => {
    if (!phone) return "";

    let value = String(phone).replace(/\D/g, "");

    /*
     * Default India country code when a 10-digit
     * Indian mobile number is stored without +91.
     */
    if (value.length === 10) {
      value = `91${value}`;
    }

    return value;
  };

  const openWhatsApp = (lead) => {
    const phone = normalizePhone(getPhone(lead));

    if (!phone) {
      return;
    }

    const name = getLeadName(lead);

    const message = encodeURIComponent(
      `Hello ${name}, this is from the admissions team.`
    );

    window.open(
      `https://wa.me/${phone}?text=${message}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const filteredLeads = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return leads
      .filter((lead) => {
        const name = getLeadName(lead).toLowerCase();

        const phone = String(
          getPhone(lead)
        ).toLowerCase();

        const email = String(
          lead?.email || ""
        ).toLowerCase();

        return (
          !keyword ||
          name.includes(keyword) ||
          phone.includes(keyword) ||
          email.includes(keyword)
        );
      })
      .sort((a, b) => {
        const aDate = new Date(
          a?.updatedAt ||
            a?.createdAt ||
            0
        );

        const bDate = new Date(
          b?.updatedAt ||
            b?.createdAt ||
            0
        );

        return bDate - aDate;
      });
  }, [leads, search]);

  const selectedActivities = useMemo(() => {
    if (!selectedLead) return [];

    const selectedId =
      selectedLead?._id ||
      selectedLead?.id;

    return activities
      .filter(
        (activity) =>
          String(activity?.leadId) ===
          String(selectedId)
      )
      .sort(
        (a, b) =>
          new Date(a?.createdAt || 0) -
          new Date(b?.createdAt || 0)
      );
  }, [activities, selectedLead]);

  const leadsWithWhatsApp = useMemo(() => {
    return leads.filter(
      (lead) => !!getPhone(lead)
    ).length;
  }, [leads]);

  const whatsappActivities = activities.length;

  const todayActivities = activities.filter(
    (activity) => {
      if (!activity?.createdAt) return false;

      const date = new Date(
        activity.createdAt
      );

      const today = new Date();

      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() ===
          today.getFullYear()
      );
    }
  ).length;

  const formatDate = (date) => {
    if (!date) return "—";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }

    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="mb-1">WhatsApp</h4>

            <p className="text-muted mb-0">
              Manage WhatsApp communication with your leads.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-light"
            onClick={loadWhatsAppData}
            disabled={loading}
          >
            <FiRefreshCw
              className={`me-1 ${
                loading ? "spin" : ""
              }`}
            />
            Refresh
          </button>
        </div>

        {/* Summary */}
        <div className="row">

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">

                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiMessageCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      WhatsApp Leads
                    </p>

                    <h4 className="mb-0">
                      {leadsWithWhatsApp}
                    </h4>
                  </div>

                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">

                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiMessageCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      WhatsApp Activities
                    </p>

                    <h4 className="mb-0">
                      {whatsappActivities}
                    </h4>
                  </div>

                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">

                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiPhone />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Leads With Number
                    </p>

                    <h4 className="mb-0">
                      {leadsWithWhatsApp}
                    </h4>
                  </div>

                </div>

              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">

                <div className="d-flex align-items-center">

                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiCheckCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Today's Activity
                    </p>

                    <h4 className="mb-0">
                      {todayActivities}
                    </h4>
                  </div>

                </div>

              </div>
            </div>
          </div>

        </div>

        <div className="row">

          {/* Lead List */}
          <div className="col-xl-5">

            <div className="card">

              <div className="card-header">

                <div className="search-box">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search lead, phone or email..."
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                  />

                  <FiSearch className="search-icon" />
                </div>

              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "650px",
                  overflowY: "auto",
                }}
              >

                {loading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading...
                      </span>
                    </div>
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-5 px-3">

                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title bg-light text-muted rounded-circle fs-1">
                        <FiUser />
                      </div>
                    </div>

                    <h5>No leads found</h5>

                    <p className="text-muted mb-0">
                      No leads match your search.
                    </p>

                  </div>
                ) : (
                  filteredLeads.map((lead, index) => {

                    const id =
                      lead?._id ||
                      lead?.id;

                    const isSelected =
                      String(
                        selectedLead?._id ||
                          selectedLead?.id
                      ) === String(id);

                    const phone =
                      getPhone(lead);

                    return (
                      <div
                        key={id || index}
                        className={`p-3 border-bottom ${
                          isSelected
                            ? "bg-light"
                            : ""
                        }`}
                      >

                        <div className="d-flex align-items-center">

                          <button
                            type="button"
                            className="btn btn-link p-0 border-0"
                            onClick={() =>
                              setSelectedLead(
                                lead
                              )
                            }
                          >
                            <div className="avatar-sm">
                              <span className="avatar-title rounded-circle bg-success-subtle text-success">
                                {getLeadName(
                                  lead
                                )
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          </button>

                          <div
                            className="flex-grow-1 ms-3"
                            style={{
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setSelectedLead(
                                lead
                              )
                            }
                          >

                            <h6 className="mb-1">
                              {getLeadName(
                                lead
                              )}
                            </h6>

                            <p className="text-muted mb-0 small">
                              {phone ||
                                lead?.email ||
                                "No contact information"}
                            </p>

                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={!phone}
                            onClick={() =>
                              openWhatsApp(
                                lead
                              )
                            }
                            title={
                              phone
                                ? "Open WhatsApp"
                                : "No WhatsApp number"
                            }
                          >
                            <FiMessageCircle />
                          </button>

                        </div>

                      </div>
                    );
                  })
                )}

              </div>

            </div>

          </div>

          {/* Conversation */}
          <div className="col-xl-7">

            <div className="card">

              {!selectedLead ? (
                <div className="card-body text-center py-5">

                  <div className="avatar-lg mx-auto mb-3">
                    <div className="avatar-title bg-success-subtle text-success rounded-circle fs-1">
                      <FiMessageCircle />
                    </div>
                  </div>

                  <h5>Select a lead</h5>

                  <p className="text-muted mb-0">
                    Select a lead to view its recorded
                    WhatsApp activity.
                  </p>

                </div>
              ) : (
                <>
                  {/* Lead Header */}
                  <div className="card-header">

                    <div className="d-flex align-items-center">

                      <div className="avatar-md flex-shrink-0">
                        <span className="avatar-title rounded-circle bg-success-subtle text-success fs-4">
                          {getLeadName(
                            selectedLead
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>

                      <div className="flex-grow-1 ms-3">

                        <h5 className="mb-1">
                          {getLeadName(
                            selectedLead
                          )}
                        </h5>

                        <p className="text-muted mb-0">
                          {getPhone(
                            selectedLead
                          ) ||
                            "No WhatsApp number"}
                        </p>

                      </div>

                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={
                          !getPhone(
                            selectedLead
                          )
                        }
                        onClick={() =>
                          openWhatsApp(
                            selectedLead
                          )
                        }
                      >
                        <FiMessageCircle className="me-1" />
                        WhatsApp
                      </button>

                    </div>

                  </div>

                  {/* Activity */}
                  <div className="card-body">

                    {selectedActivities.length === 0 ? (
                      <div className="text-center py-5">

                        <FiMessageCircle
                          size={34}
                          className="text-muted mb-3"
                        />

                        <h6>
                          No WhatsApp activity recorded
                        </h6>

                        <p className="text-muted mb-3">
                          There is currently no WhatsApp
                          activity stored for this lead.
                        </p>

                        {getPhone(
                          selectedLead
                        ) && (
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={() =>
                              openWhatsApp(
                                selectedLead
                              )
                            }
                          >
                            <FiMessageCircle className="me-1" />
                            Start WhatsApp Chat
                          </button>
                        )}

                      </div>
                    ) : (
                      <div>

                        {selectedActivities.map(
                          (activity, index) => (
                            <div
                              className="d-flex mb-4"
                              key={
                                activity?._id ||
                                activity?.id ||
                                index
                              }
                            >

                              <div className="avatar-sm flex-shrink-0">
                                <span className="avatar-title rounded-circle bg-success-subtle text-success">
                                  <FiMessageCircle />
                                </span>
                              </div>

                              <div className="flex-grow-1 ms-3">

                                <div className="d-flex justify-content-between">

                                  <h6 className="mb-1">
                                    WhatsApp
                                  </h6>

                                  <small className="text-muted">
                                    {formatDate(
                                      activity?.createdAt
                                    )}
                                  </small>

                                </div>

                                <div className="p-3 bg-light rounded">
                                  {activity?.description ||
                                    "No message details recorded."}
                                </div>

                              </div>

                            </div>
                          )
                        )}

                      </div>
                    )}

                  </div>
                </>
              )}

            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default WhatsApp;